import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money, TYPE_LABELS, STATUS_LABELS } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VerificationCard } from "@/components/VerificationCard";
import { RoleChooser, roleLabel } from "@/components/RoleChooser";

const STATUS_CLS = { pending: "bg-amber-100 text-amber-800", approved: "bg-emerald-100 text-emerald-800", rejected: "bg-red-100 text-red-700" };
export const StatusBadge = ({ s }) => <span className={`text-xs font-bold rounded-lg px-2.5 py-1 ${STATUS_CLS[s]}`}>{STATUS_LABELS[s]}</span>;
const Empty = ({ t }) => <div className="text-center py-12 text-slate-500 bg-white border rounded-xl">{t}</div>;

const Profile = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [company, setCompany] = useState(user.company_name || "");
  const save = async () => {
    try { const { data } = await api.put("/me", { name, phone, company_name: company }); setUser(data); toast.success("تم حفظ الملف"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white border rounded-xl p-5 grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end" data-testid="profile-form">
      <div><label className="text-xs font-semibold">الاسم</label><Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-xl mt-1" /></div>
      <div><label className="text-xs font-semibold">اسم الشركة / المنشأة</label><Input data-testid="profile-company-input" value={company} onChange={(e) => setCompany(e.target.value)} className="h-11 rounded-xl mt-1" /></div>
      <div><label className="text-xs font-semibold">رقم الجوال (مطلوب للمزايدة)</label><Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} dir="ltr" className="h-11 rounded-xl mt-1 text-right" /></div>
      <Button data-testid="profile-save-btn" onClick={save} className="h-11 rounded-lg bg-brand hover:bg-brand-dark px-8">حفظ</Button>
    </div>
  );
};

const MyListings = ({ items, reload }) => {
  const del = async (id) => {
    if (!window.confirm("حذف الإعلان؟")) return;
    try { await api.delete(`/listings/${id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!items.length) return <Empty t="لم تضف أي إعلان بعد." />;
  return (
    <div className="space-y-3">
      {items.map((l) => (
        <div key={l.id} className="bg-white border rounded-xl p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`my-listing-${l.id}`}>
          <div className="min-w-0">
            <Link to={`/listing/${l.id}`} className="font-bold hover:text-brand">{l.title}</Link>
            <div className="text-xs text-slate-500 mt-1">{l.ref_code && <span className="font-mono" dir="ltr">{l.ref_code} · </span>}{TYPE_LABELS[l.type]}{l.type === "auction" && l.current_bid ? ` — أعلى مزايدة ${money(l.current_bid)} (عمولة ${money(l.commission)})` : ""}</div>
          </div>
          <div className="flex items-center gap-2"><StatusBadge s={l.status} />
            <button onClick={() => del(l.id)} data-testid={`delete-listing-${l.id}`} className="p-2 text-slate-400 hover:text-red-600"><Trash2 size={16} /></button></div>
        </div>
      ))}
    </div>
  );
};

const MyBids = ({ items }) => !items.length ? <Empty t="لا توجد مزايدات." /> : (
  <div className="space-y-3">
    {items.map((b) => (
      <Link to={`/listing/${b.listing_id}`} key={b.id} className="bg-white border rounded-xl p-4 flex flex-wrap items-center justify-between gap-2" data-testid={`my-bid-${b.id}`}>
        <div><div className="font-bold">{b.listing_title}</div><div className="text-xs text-slate-500">مزايدتك: {money(b.amount)} — الأعلى حالياً: {money(b.current_bid)}</div></div>
        {b.is_top ? <span className="text-xs font-bold text-emerald-700 flex items-center gap-1"><Trophy size={14} />{b.is_ended ? `فزت — العمولة ${money(b.current_bid * 0.01)}` : "الأعلى"}</span>
          : <span className="text-xs text-slate-500">{b.is_ended ? "انتهى" : "تم تجاوزك"}</span>}
      </Link>
    ))}
  </div>
);

const MyContacts = ({ items }) => !items.length ? <Empty t="لا توجد طلبات تواصل." /> : (
  <div className="space-y-3">
    {items.map((c) => (
      <Link to={`/listing/${c.listing_id}`} key={c.id} className="bg-white border rounded-xl p-4 flex items-center justify-between gap-2" data-testid={`my-contact-${c.id}`}>
        <div className="font-bold">{c.listing_title}</div><StatusBadge s={c.status} />
      </Link>
    ))}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState({ listings: [], bids: [], contacts: [] });
  const load = () => Promise.all([api.get("/my/listings"), api.get("/my/bids"), api.get("/my/contact-requests")])
    .then(([a, b, c]) => setD({ listings: a.data, bids: b.data, contacts: c.data })).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-3xl font-bold tracking-tight">مرحباً، {user.name}</h1><p className="text-slate-500 text-sm">{user.email}</p></div>
        {(user.is_admin || user.role !== "buyer") && <Link to="/add" data-testid="dashboard-add-btn" className="bg-sun hover:bg-sun-dark text-white rounded-lg px-6 py-2.5 font-bold text-sm">أضف إعلان</Link>}
      </div>
      {!user.is_admin && (
        <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="role-card">
          <div className="font-bold">نوع الحساب: <span className="text-brand" data-testid="current-role">{roleLabel(user.role) || "لم يحدد بعد"}</span></div>
          <RoleChooser />
        </div>
      )}
      <VerificationCard />
      <Profile />
      <Tabs defaultValue="listings" dir="rtl">
        <TabsList className="rounded-lg">
          <TabsTrigger value="listings" data-testid="tab-my-listings" className="rounded-lg">إعلاناتي ({d.listings.length})</TabsTrigger>
          <TabsTrigger value="bids" data-testid="tab-my-bids" className="rounded-lg">مزايداتي ({d.bids.length})</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-my-contacts" className="rounded-lg">طلبات التواصل ({d.contacts.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="listings" className="mt-5"><MyListings items={d.listings} reload={load} /></TabsContent>
        <TabsContent value="bids" className="mt-5"><MyBids items={d.bids} /></TabsContent>
        <TabsContent value="contacts" className="mt-5"><MyContacts items={d.contacts} /></TabsContent>
      </Tabs>
    </div>
  );
}
